import { useState, useEffect } from 'react';
import { createResource,getResources,deleteResource, updateResource} from '../../../router/api';
import toast, { Toaster } from 'react-hot-toast';

interface Emplacement {
  id: number;
  nom: string;
  code?: string;
}

interface LigneBC {
  id: number;
  produit_id: number;
  produit: string;
  quantite: number;
  quantite_recue: number;
  prix_unitaire_ht: number;
}

interface PurchaseOrder {
  id: number;
  numero: string;
  fournisseur: string;
  lignes: LigneBC[];
}

interface ReceptionLine {
  ligne_bc_id: number;
  produit_id: number;
  produit: string;
  prix_unitaire_ht: number;
  stock_actuel: number;

  quantite_commandee: number;
  quantite_recue: number;
  quantite_restante: number;

  quantite_receptionnee: number;
  quantite_conforme: number;
  quantite_rejetee: number;
  motif_rejet?: string;
  action_non_conformite?: 'retour' | 'acceptation' | 'rebut' | '';
}

export default function ReceptionForm({ 
  purchaseOrder, 
  onClose, 
  onSave 
}: { 
  purchaseOrder: PurchaseOrder; 
  onClose: () => void; 
  onSave?: (data: any) => void 
}) {
  const [lines, setLines] = useState<ReceptionLine[]>([]);
  const [observations, setObservations] = useState('');
  const [emplacementId, setEmplacementId] = useState<string>('');
  const [emplacements, setEmplacements] = useState<Emplacement[]>([]);
  const [loadingEmplacements, setLoadingEmplacements] = useState(true);

  // Chargement des emplacements
  useEffect(() => {
    getResources('emplacements-stock')
      .then(res => setEmplacements(res.data.data || res.data))
      .catch(() => toast.error("Erreur lors du chargement des emplacements"))
      .finally(() => setLoadingEmplacements(false));
  }, []);

  // Initialisation des lignes
useEffect(() => {
  if (purchaseOrder?.lignes) {
    const mapped = purchaseOrder.lignes.map((l: any) => ({
      ligne_bc_id: l.id,
      produit_id: l.produit_id,
      produit: l.produit_nom,           // ✅ correction
      prix_unitaire_ht: l.prix_unitaire_ht,
      stock_actuel: l.stock_actuel ?? 0, // ✅ renvoyé par backend

      quantite_commandee: l.quantite,
      quantite_recue: l.quantite_recue ?? 0,
      quantite_restante: l.quantite - (l.quantite_recue ?? 0),

      // champs de réception à remplir par l'utilisateur
      quantite_receptionnee: 0,
      quantite_conforme: 0,
      quantite_rejetee: 0,
      motif_rejet: "",
      action_non_conformite: "" as const,
    }));

    setLines(mapped);
  }
}, [purchaseOrder]);


  const updateLine = (ligne_bc_id: number, field: keyof ReceptionLine, value: any) => {
    setLines(prev => prev.map(line => {
      if (line.ligne_bc_id !== ligne_bc_id) return line;

      const updated = { ...line, [field]: value };

      const total = updated.quantite_receptionnee || 0;
      const rejete = updated.quantite_rejetee || 0;
      updated.quantite_conforme = Math.max(0, total - rejete);

      return updated;
    }));
  };

  const submit = async () => {
    const avecQte = lines.filter(l => l.quantite_receptionnee > 0);

    if (avecQte.length === 0) {
      return toast.error("Veuillez saisir au moins une quantité réceptionnée");
    }

    if (!emplacementId) {
      return toast.error("L'emplacement de stockage est obligatoire");
    }

    // Vérification stricte : quantité reçue > quantité restante = ALERTE
    for (const l of avecQte) {
      if (l.quantite_receptionnee > l.quantite_restante) {
        toast.error(
          `Quantité réceptionnée supérieure à commandée pour "${l.produit}"\n` +
          `Commandé : ${l.quantite_commandee} | Reçu précédemment : ${l.quantite_recue} | Restant : ${l.quantite_restante}\n` +
          `Vous ne pouvez pas saisir plus que le restant.`
        );
        return;
      }

      if (l.quantite_rejetee > 0) {
        if (!l.motif_rejet?.trim()) {
          toast.error(`Motif obligatoire pour "${l.produit}"`);
          return;
        }
        if (!l.action_non_conformite || l.action_non_conformite === '') {
          toast.error(`Action obligatoire pour la non-conformité de "${l.produit}"`);
          return;
        }
      }
    }

    const payload = {
      bc_fournisseur_id: purchaseOrder.id,
      date_reception: new Date().toISOString().split('T')[0],
      observations: observations || null,
      emplacement_id: Number(emplacementId),

      lignes: avecQte.map(l => ({
        ligne_bc_id: l.ligne_bc_id,
        produit_id: l.produit_id,
        quantite_recue: l.quantite_conforme,
        quantite_rejetee: l.quantite_rejetee,
        motif_rejet: l.quantite_rejetee > 0 ? l.motif_rejet : null,
        prix_unitaire_ht: l.prix_unitaire_ht,
      })),
    };

    try {
      const res = await createResource('bons-reception', payload);
      
      toast.success("Bon de réception créé avec succès ! Stock mis à jour.");
      onSave?.(res.data);
      onClose();
    } catch (err: any) {
      const msg = err.response?.data?.message || "Erreur lors de l'enregistrement";
      toast.error(msg);
    }
  };

  const getRowClass = (l: ReceptionLine) => {
    if (l.quantite_receptionnee > l.quantite_restante) return 'bg-red-100 border-l-4 border-red-500';
    if (l.quantite_rejetee > 0) return 'bg-orange-50';
    return '';
  };

  return (
    <>
      <Toaster position="top-right" />

      <div className="bg-white rounded-lg shadow-lg border border-gray-200 max-w-7xl mx-auto">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center bg-gradient-to-r from-green-600 to-green-700 text-white rounded-t-lg">
          <div>
            <h2 className="text-2xl font-bold">Réception de Marchandise</h2>
            <p className="text-sm opacity-90">
              BC N° {purchaseOrder.numero} — {purchaseOrder.fournisseur}
            </p>
          </div>
          <button onClick={onClose} className="text-white hover:bg-white/20 p-2 rounded">
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>

        <div className="p-6 space-y-6">

          {/* Observations */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Observations générales <span className="text-gray-500">(facultatif)</span>
            </label>
            <textarea
              value={observations}
              onChange={e => setObservations(e.target.value)}
              rows={4}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              placeholder="État des colis, retard, anomalies, remarques diverses..."
            />
          </div>

          {/* Emplacement */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Emplacement de stockage <span className="text-red-500">*</span>
            </label>
            {loadingEmplacements ? (
              <p className="text-sm text-gray-500">Chargement des emplacements...</p>
            ) : (
              <select
                value={emplacementId}
                onChange={e => setEmplacementId(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                required
              >
                <option value="">— Choisir un emplacement —</option>
                {emplacements.map(e => (
                  <option key={e.id} value={e.id}>
                    {e.nom} {e.code ? `(${e.code})` : ''}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Tableau */}
          <div className="overflow-x-auto border rounded-lg">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Produit</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Cmd</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Déjà reçue</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Restant</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Récept° totale</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-green-600 font-bold uppercase">Conforme</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-red-600 font-bold uppercase">Non conf.</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action & Motif</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {lines.map(line => (
                  <tr key={line.ligne_bc_id} className={getRowClass(line)}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{line.produit}</div>
                      <div className="text-xs text-gray-500">Stock actuel : {line.stock_actuel}</div>
                    </td>
                    <td className="px-4 py-3 text-center font-medium">{line.quantite_commandee}</td>
                    <td className="px-4 py-3 text-center">{line.quantite_recue}</td>
                    <td className="px-4 py-3 text-center font-bold text-blue-600">{line.quantite_restante}</td>

                    <td className="px-4 py-3 text-center">
                      <input
                        type="number"
                        min="0"
                        max={line.quantite_restante}
                        value={line.quantite_receptionnee}
                        onChange={e => updateLine(line.ligne_bc_id, 'quantite_receptionnee', Number(e.target.value) || 0)}
                        className={`w-24 px-3 py-1.5 border rounded text-center font-medium ${
                          line.quantite_receptionnee > line.quantite_restante ? 'border-red-500 bg-red-50' : 'border-gray-300'
                        }`}
                      />
                      {line.quantite_receptionnee > line.quantite_restante && (
                        <p className="text-xs text-red-600 text-xs mt-1">Supérieur au restant !</p>
                      )}
                    </td>

                    <td className="px-4 py-3 text-center font-bold text-green-600 text-lg">
                      {line.quantite_conforme}
                    </td>

                    <td className="px-4 py-3 text-center">
                      <input
                        type="number"
                        min="0"
                        max={line.quantite_receptionnee}
                        value={line.quantite_rejetee}
                        onChange={e => updateLine(line.ligne_bc_id, 'quantite_rejetee', Number(e.target.value) || 0)}
                        className="w-20 px-3 py-1.5 border border-gray-300 rounded text-center"
                      />
                    </td>

                    <td className="px-4 py-3">
                      {line.quantite_rejetee > 0 && (
                        <div className="space-y-2">
                          <select
                            value={line.action_non_conformite || ''}
                            onChange={e => updateLine(line.ligne_bc_id, 'action_non_conformite', e.target.value)}
                            className="text-xs border border-gray-300 rounded px-2 py-1 w-full"
                          >
                            <option value="">Choisir une action</option>
                            <option value="retour">Retour fournisseur</option>
                            <option value="acceptation">Accepté avec réserve</option>
                            <option value="rebut">Rebut / Destruction</option>
                          </select>
                          <input
                            type="text"
                            placeholder="Motif détaillé..."
                            value={line.motif_rejet || ''}
                            onChange={e => updateLine(line.ligne_bc_id, 'motif_rejet', e.target.value)}
                            className="text-xs border border-gray-300 rounded px-2 py-1 w-full"
                            required={line.quantite_rejetee > 0}
                          />
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex justify-end gap-4 pt-6 border-t">
            <button
              onClick={onClose}
              className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium"
            >
              Annuler
            </button>
            <button
              onClick={submit}
              className="px-8 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium text-lg"
            >
              Valider la Réception
            </button>
          </div>
        </div>
      </div>
    </>
  );
}